import { Backdrop, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Modal, Select, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { MainContext } from '../../../../context/main/main-context';
import DataHandler from '../../../../handlers/DataHandler';
import { sweetStatusAlert } from '../../../../components/common/SweetAlert';

function AddTemplate({ isOpen, setIsOpen, currentTemplate, setCurrentTemplate, handlefetchAllTemplates }) {
    const { createTemplate, addTemplate, updateTemplate, handleUpdateTemplate, templateMetaData} = useContext(MainContext);
    const [loading, setLoading] = useState(false);
    const [newTemplate, setNewTemplate] = useState({
        name: currentTemplate?.name || '',
        description: currentTemplate?.description || '',
        no_of_parts: currentTemplate?.no_of_parts || 0,
        status_id: currentTemplate?.status_id === 0? 0: 1,
        part_notation_id: currentTemplate?.part_notation_id || 2
    });
    const [errors, setErrors] = useState({});

    const handleChange = (field) => (e) => {
        if (!e) return;
        let value = e.target ? e.target.value : e;
        if (field === 'no_of_parts') {
            value = parseInt(value) || 0;
        }
        setNewTemplate(prev => ({ ...prev, [field]: value }));
        // validate on every change
        validateTemplate(field, value);
    };

    const handleCreate = async () => {
        try {
            setLoading(true);
            const user_id = DataHandler.getFromSession('manage_user_id');
            await createTemplate?.(newTemplate, user_id);
        } catch (error) {
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to create template", "error");
        }
    };

    const handleUpdate = async () => {
        try {
            setLoading(true);
            const user_id = DataHandler.getFromSession('manage_user_id');
            await handleUpdateTemplate?.(newTemplate, user_id, currentTemplate.template_id);
        } catch (error) {
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to update template", "error");
        }
    };

    const handleClose = () => {
            setNewTemplate({
                name: '',
                description: '',
                no_of_parts: 0,
                status_id: 1
            });
            setCurrentTemplate(null)
            setIsOpen(false);
    };

    const validateTemplate = (field, value) => {
        let error = "";

        switch (field) {
            case "name":
                if (!value) {
                    error = "Template Name is required.";
                }
                const nameExists = templateMetaData?.templates?.some(
                    (template) => template.name.trim().toLowerCase() === value.trim().toLowerCase() && 
                    template.template_id !== currentTemplate?.template_id 
                );
                if (nameExists) {
                    error = "Template name already exists.";
                }
                break;

            case "status_id":
                if (!value) {
                    error = "Status is required.";
                }
                break;

            case "no_of_parts":
                if (!value || value < 1) {
                    error = "Enter valid number of parts (min 1).";
                }
                break;

            default:
                break;
        }

        setErrors(prev => ({ ...prev, [field]: error }));
        return error;
    };

    useEffect(() => {
        if (addTemplate && addTemplate.success) {
            setLoading(true);
            sweetStatusAlert("Success", "Template created successfully", "success");
            handlefetchAllTemplates()
            handleClose();
        }
        else if(addTemplate && !addTemplate.success){
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to created template", "error");
        }
    }, [addTemplate]);

    useEffect(() => {
        if (updateTemplate && updateTemplate.success) {
            setLoading(true);
            sweetStatusAlert("Success", "Template updated successfully", "success");
            handlefetchAllTemplates()
            handleClose();
        }
        else if(updateTemplate && !updateTemplate.success){
            setLoading(false);
            sweetStatusAlert("Failed", "Failed to update template", "error");
        }
    }, [updateTemplate]);

    useEffect(() => {
        Object.keys(newTemplate).forEach(field => {
            validateTemplate(field, newTemplate[field]);
        });
    }, []);
    
    const isFormValid = () => {
        return Object.values(errors).every(err => !err)
            && newTemplate.name
            && newTemplate.no_of_parts >= 1;
    };

    return (
        <Modal 
            open={isOpen} 
            onClose={handleClose}
            disableEscapeKeyDown={loading} 
            sx={{
                
                '& .MuiBackdrop-root': {
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: -1 
                }
            }}
        >
            <Box 
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: 800,
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                    zIndex: 1,
                    overflowY: 'auto',
                }}
            >
            
                <Backdrop
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: (theme) => theme.zIndex.modal + 1,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        borderRadius: '8px'
                    }}
                    open={loading}
                >
                    <CircularProgress color="primary" />
                </Backdrop>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                        sx={{ mb: 3 }}
                        value={newTemplate.name}
                        onChange={handleChange("name")}
                        label="Template Name"
                        variant="outlined"
                        disabled={loading}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                    />

                    <TextField
                        sx={{ mb: 3 }}
                        value={newTemplate.description}
                        onChange={handleChange("description")}
                        label="Description"
                        variant="outlined"
                        disabled={loading}
                    />

                    <TextField
                        sx={{ mb: 3 }}
                        value={newTemplate.no_of_parts}
                        onChange={handleChange("no_of_parts")}
                        label="No of parts"
                        variant="outlined"
                        type="number"
                        inputProps={{ min: 1 }}
                        disabled={loading}
                        error={Boolean(errors.no_of_parts)}
                        helperText={errors.no_of_parts}
                    />

                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Part Notation</InputLabel>
                        <Select
                            value={newTemplate.part_notation_id || 2}
                            onChange={handleChange("part_notation_id")}
                            label="Part Notation"
                            disabled={loading}
                        >
                            {templateMetaData?.notations?.map((notation) => (
                                <MenuItem key={notation.notation_id} value={notation.notation_id}>
                                    {notation.notation_type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={newTemplate.status_id}
                            onChange={handleChange("status_id")}
                            label="Status"
                            disabled={loading}
                        >
                            {[{ value: "Active", key: 1 }, { value: "Inactive", key: 0 }].map(
                                (option) => (
                                <MenuItem key={option.key} value={option.key}>
                                    {option.value}
                                </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                </FormControl>

                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:2}}>
                    {currentTemplate && currentTemplate.template_id ? (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleUpdate} 
                            ddisabled={!isFormValid() || loading}
                        >
                            Update
                        </Button>
                    ):(
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={handleCreate} 
                            disabled={!isFormValid() || loading}
                        >
                            Create
                        </Button>
                    )}
                    
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={handleClose} 
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                </div>
            </Box>
        </Modal>
    );
}

export default AddTemplate;